<template>
    <div id="main-wrapper">
        <NavbarDark/>

        <section class="bg-primary position-relative">
            <div class="position-absolute start-0 top-0 w-25 h-15 bg-light rounded-end-pill opacity-25 mt-4"></div>
            <div class="position-absolute start-0 bottom-0 w-15 h-20 bg-light rounded-top-pill opacity-25 ms-4"></div>
            <div class="position-absolute end-0 top-0 w-15 h-25 bg-light rounded-bottom-pill opacity-25 me-4"></div>
            <div class="position-absolute end-0 bottom-0 w-25 h-15 bg-light rounded-start-pill opacity-25 mb-4"></div>
            <div class="ht-30"></div>
            <div class="ht-30"></div>
        </section>
        
        <section class="gray-simple">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-12 col-md-12">
                        <p><strong>{{ $t('Visitor Count') }}</strong> {{ visitorCount }}</p>
                    </div>
                </div>
                <div class="row justify-content-center g-4">
                    <div v-if="stands.length === 0">
                        <p>{{ $t('There is no stands until now!') }}</p>
                    </div>
                    <div v-for="(item, index) in stands" :key="index" class="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                        <list-stand :item="item"/>
                    </div>
                </div>
                
                <div id="scroll-sentinel" class="text-center my-4">
                        <span v-if="loading">{{ $t('Downloading..') }}</span>
                </div>
                <div class="tags-row">
                    <strong>{{ $t('Tags') }}:</strong>
                    <span v-for="tag in hall?.tags" :key="tag.id" class="tag-item">{{ tag.name }}</span>
                </div>
            </div>		
        </section>

        <FooterTop :bg="'theme-bg'"/>

        <FooterDark/>

        <ScrollToTop/>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ListStand from '@/components/property/list-stand.vue';
import axios from 'axios';
import { useI18n } from "vue-i18n";
import { useHead } from '@vueuse/head'
import ScrollToTop from '@/components/scroll-to-top.vue';
import NavbarDark from "@/components/navbar/navbar-dark.vue"
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue'
const route = useRoute();
const router = useRouter();

const hallSlug = computed(() => route.params.slug);

const { locale } = useI18n();

const stands = ref([]);
const hall = ref(null);

const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(false);

const observer = ref(null);

const visitorCount = ref(0);


const initPage = async () => {

    stands.value = [];
    currentPage.value = 1;
    totalPages.value = 1;

    await fetchHall();

    await Promise.all([
        fetchStands(),
        fetchVisitorCount(),
        trackVisitor()
    ]);

    setupObserver();
};

const fetchHall = async () => {
    try {

        const res = await axios.get(
            `/hall-by-slug/${hallSlug.value}`,
            {
                params: {
                    lang: locale.value
                }
            }
        );

        hall.value = res.data.data; 

    } catch (error) {
        console.error(error);
    }
};

const fetchStands = async () => {

    if (!hallSlug.value) return;
    if (loading.value) return;
    if (currentPage.value > totalPages.value) return;

    loading.value = true;

    try {

        const response = await axios.get(
            `/stands-of-hall/${hallSlug.value}`,
            {
                params: {
                    page: currentPage.value,
                    lang: locale.value
                }
            }
        );

        stands.value.push(...response.data.data);
        totalPages.value = response.data.meta.last_page;
        currentPage.value++;
        
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};


const trackVisitor = async () => {

    if (!hall.value?.id) return;

    let visitorKey = localStorage.getItem('visitor_key');

    if (!visitorKey) {
        visitorKey = crypto.randomUUID();
        localStorage.setItem('visitor_key', visitorKey);
    }

    try {
        await axios.post(
            `/visitor/hall/${hall.value.id}`,
            {},
            {
                headers: { 'X-Visitor-Key': visitorKey }
            }
        );
    } catch (error) {
        console.error(error);
    }
};

const fetchVisitorCount = async () => {

    if (!hall.value?.id) return;

    try {
        const res = await axios.get(`/visitor/hall/${hall.value.id}`);
        visitorCount.value = res.data.visitor_count;

    } catch (error) {
        console.error(error);
    }
};

///// SEO && Google
const baseUrl = 'https://ruqm.me'

const canonicalUrl = computed(() => {
  if (!hall.value?.slug?.[locale.value]) return ''
  return `${baseUrl}/${locale.value}/hall/${hall.value.slug[locale.value]}`
})

const hreflangLinks = computed(() => {
  if (!hall.value?.slug) return []

  return Object.entries(hall.value.slug).map(([lang, slug]) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}/hall/${slug}`
  }))
})

const xDefault = computed(() => {
  const enSlug = hall.value?.slug?.en
  if (!enSlug) return null

  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/en/hall/${enSlug}`
  }
})

useHead(() => {
  if (!hall.value?.id) return {}

  return {
    title: hall.value.seo_title || hall.value.name,
    meta: [
      {
        name: 'description',
        content: hall.value.seo_description || ''
      },
      {
        name: 'keywords',
        content: hall.value.focus_keywords?.join(', ') || ''
      },


      { property: 'og:type', content: 'event' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:title', content: hall.value.seo_title },
      { property: 'og:description', content: hall.value.seo_description },
      { property: 'og:image', content: hall.value.seo_image },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: hall.value.seo_title },
      { name: 'twitter:description', content: hall.value.seo_description },
      { name: 'twitter:image', content: hall.value.seo_image },
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
      ...(xDefault.value ? [xDefault.value] : [])
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": hall.value.name || 'Hall',
        "description": hall.value.seo_description,
        "image": hall.value.logo || [],
        "url": `https://ruqm.me${route.path}`,
        "address": hall.value.location || 'Unknown location'
        }),
        key: "schema"
      }
    ]
  }
})

const setupObserver = async () => {

    await nextTick();

    const sentinel = document.getElementById('scroll-sentinel');
    if (!sentinel) return;

    observer.value?.disconnect();

    observer.value = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
            fetchStands();
        }
    }, {
        rootMargin: "200px"
    });

    observer.value.observe(sentinel);
};

watch(
    [() => route.params.slug, locale],
    async () => {
        await initPage();
    },
    { deep: true, immediate: false }
);

watch(locale, async (newLocale) => {

    if (!hall.value?.slug) return;

    const localizedSlug = hall.value.slug[newLocale];
    if (!localizedSlug) {
        await router.push({ name: 'NotFound' });
        return;
    }
    const newPath = `/${newLocale}/hall/${localizedSlug}`;

    if (route.fullPath !== newPath) {
        await router.replace(newPath);
    }
    await initPage()
});

onMounted(async () => {
    await initPage();
});

onUnmounted(() => {
    observer.value?.disconnect();
});
</script>

<style scoped>
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-item {
  background-color: #C48B6E;
  color: #fff;
  padding: 0px 10px;
  border-radius: 12px;
  font-size: 0.9rem;
}
</style>